// src/pages/Contrats.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Search, Printer, Loader2, CheckCircle, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Eye, Pencil, Trash2, X } from 'lucide-react';
import { customersService, metersService, contractsService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
const NIVEAUX = [
  { key: 'S1', label: 'S1 — SHS Forfait 3 000 F CFA / 50 W' },
  { key: 'S2', label: 'S2 — SHS Forfait 5 000 F CFA / 100 W' },
  { key: 'S3', label: 'S3 — SHS Forfait 8 000 F CFA / 150 W' },
  { key: 'S4_SHS', label: 'S4 — SHS Compteur 120 F/kWh / 200 W' },
  { key: 'S4_CENTRALE', label: 'S4 — Centrale électrique 120 F/kWh / 2 000 W' },
  { key: 'AGR', label: 'AGR — Centrale & SHS 180 F/kWh / 5 000 W' },
];

const PAGE_SIZE = 10;

export default function Contrats() {

  const { user } = useAuth();
  const canAdd = hasPermission(user, PERMISSIONS.ADD_CONTRACT);
  const canEdit = hasPermission(user, PERMISSIONS.EDIT_CONTRACT);
  const canDelete = hasPermission(user, PERMISSIONS.DELETE_CONTRACT);
const [lastGeneratedNumber, setLastGeneratedNumber] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // ---- Recherche client ----
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customer, setCustomer] = useState(null);
  const customerTimeout = useRef(null);

  // ---- Recherche compteur (saisie libre, comme le client) ----
  const [meterSearch, setMeterSearch] = useState('');
  const [meterSuggestions, setMeterSuggestions] = useState([]);
  const [showMeterSuggestions, setShowMeterSuggestions] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const meterTimeout = useRef(null);
const PREFIXES = ['ZCH', 'KLD', 'TBC'];
  // const [form, setForm] = useState({
  //   prefix: 'ZCH',
  //   contract_number: '',
  //   region: '',
  //   departement: '',
  //   commune: '',
  //   quartier_village: '',
  //   niveau_service: 'S4_SHS',
  //   frais_timbres: '',
  //   frais_taxe: '',
  //   frais_branchement: '',
  //   frais_avance: '',
  //   lieu: '',
  //   date: new Date().toISOString().slice(0, 10),
  // });
const initialForm = {
  prefix: 'ZCH',
  contract_number: '',
  region: '',
  departement: '',
  commune: '',
  quartier_village: '',
  niveau_service: 'S4_SHS',
  frais_timbres: '',
  frais_taxe: '',
  frais_branchement: '',
  frais_avance: '',
  lieu: '',
  date: new Date().toISOString().slice(0, 10),
};

// ✅ AJOUT INDISPENSABLE
const [form, setForm] = useState(initialForm);

const resetForm = () => {
  setForm(initialForm);
  setCustomer(null);
  setCustomerSearch('');
  setCustomerSuggestions([]);
  setSelectedMeter(null);
  setMeterSearch('');
  setMeterSuggestions([]);
  setEditingId(null);
  refreshNextNumber(initialForm.prefix);
};

  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // ---- Liste des contrats (table paginée) ----
  const [contracts, setContracts] = useState([]);
  const [contractsTotal, setContractsTotal] = useState(0);
  const [contractsPage, setContractsPage] = useState(0); // 0-based
  const [contractsSearch, setContractsSearch] = useState('');
  const [loadingContracts, setLoadingContracts] = useState(false);

  const refreshNextNumber = useCallback((prefix) => {
    contractsService.nextNumber(prefix).then(res => {
      setForm(f => ({ ...f, contract_number: res.data.contract_number }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshNextNumber(form.prefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContracts = useCallback(async (page = contractsPage, search = contractsSearch) => {
    setLoadingContracts(true);
    try {
      const res = await contractsService.getAll({
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        search,
      });
      setContracts(res.data.items || []);
      setContractsTotal(res.data.total || 0);
    } catch (e) {
      console.error('Erreur chargement contrats', e);
    } finally {
      setLoadingContracts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadContracts(contractsPage, contractsSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsPage]);

  // ✅ Recherche automatique pendant la saisie (N° contrat, client, téléphone,
  // N° compteur, adresse) — pas besoin d'appuyer sur Entrée.
  const firstSearchRun = useRef(true);
  useEffect(() => {
    if (firstSearchRun.current) { firstSearchRun.current = false; return; }
    const t = setTimeout(() => {
      setContractsPage(0);
      loadContracts(0, contractsSearch);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsSearch]);

  // ---------------- Client ----------------
  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);
    setShowCustomerSuggestions(true);
    clearTimeout(customerTimeout.current);
    if (!value.trim()) { setCustomerSuggestions([]); return; }
    customerTimeout.current = setTimeout(async () => {
      try {
        const res = await customersService.search(value, 8);
        setCustomerSuggestions(res.data.customers || []);
      } catch (e) { /* silencieux */ }
    }, 300);
  };

  const applyCustomer = async (customerId) => {
    try {
      const res = await customersService.getById(customerId);
      const full = res.data;
      setCustomer(full);
      setCustomerSearch(`${full.full_name} — ${full.phone}`);
      setForm(f => ({
        ...f,
        quartier_village: full.neighborhood || full.address || f.quartier_village,
      }));
      return full;
    } catch (e) {
      console.error('Erreur chargement client', e);
      return null;
    }
  };

  const pickCustomer = async (c) => {
    setShowCustomerSuggestions(false);
    setPdfUrl(null);
    await applyCustomer(c.customer_id);
  };

  // ---------------- Compteur (recherche libre) ----------------
  const handleMeterSearchChange = (value) => {
    setMeterSearch(value);
    setShowMeterSuggestions(true);
    setSelectedMeter(null);
    clearTimeout(meterTimeout.current);
    if (!value.trim()) { setMeterSuggestions([]); return; }
    meterTimeout.current = setTimeout(async () => {
      try {
        const res = await metersService.getAll({ search: value, limit: 8 });
        setMeterSuggestions(res.data.items || []);
      } catch (e) { /* silencieux */ }
    }, 300);
  };

  const pickMeter = async (m) => {
    setShowMeterSuggestions(false);
    setPdfUrl(null);
    setSelectedMeter(m);
    setMeterSearch(`${m.meter_id}${m.installation_address ? ' — ' + m.installation_address : ''}`);
    setForm(f => ({
      ...f,
      departement: m.departement || f.departement,
      commune: m.commune || f.commune,
    }));
    // Le compteur entraîne automatiquement le client propriétaire (comme demandé)
    if (m.customer && m.customer.customer_id) {
      await applyCustomer(m.customer.customer_id);
    }
  };

  const updateField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const canGenerate = customer && form.niveau_service;

//  const handleGenerate = async () => {
//   if (!canGenerate) return;
//   setGenerating(true);
//   setPdfUrl(null);
//   try {
//     const payload = {
//       customer_id: customer.customer_id,
//       meter_id: selectedMeter ? selectedMeter.meter_id : null,
//       prefix: form.prefix,
//       contract_number: form.contract_number,
//       region: form.region,
//       departement: form.departement,
//       commune: form.commune,
//       quartier_village: form.quartier_village,
//       niveau_service: form.niveau_service,
//       frais_timbres: form.frais_timbres,
//       frais_taxe: form.frais_taxe,
//       frais_branchement: form.frais_branchement,
//       frais_avance: form.frais_avance,
//       lieu: form.lieu,
//       date: form.date ? new Date(form.date).toLocaleDateString('fr-FR') : '',
//     };

//     const res = editingId
//       ? await contractsService.update(editingId, payload)
//       : await contractsService.generate(payload);

//     const blob = new Blob([res.data], { type: 'application/pdf' });
//     setPdfUrl(URL.createObjectURL(blob));

//     const finalNumber = res.headers?.['x-contract-number'];
//     if (finalNumber) updateField('contract_number', finalNumber);

//     if (!editingId) {
//       refreshNextNumber(form.prefix);
//     } else {
//       setEditingId(null);
//     }
//     setContractsPage(0);
//     loadContracts(0, contractsSearch);
//   } catch (e) {
//     console.error(e);
//     alert(editingId ? "Erreur lors de la modification du contrat." : "Erreur lors de la génération du contrat.");
//   } finally {
//     setGenerating(false);
//   }
// };
  
const handleGenerate = async () => {
  if (!canGenerate) return;
  setGenerating(true);
  setPdfUrl(null);
  try {
    const payload = {
      customer_id: customer.customer_id,
      meter_id: selectedMeter ? selectedMeter.meter_id : null,
      prefix: form.prefix,
      contract_number: form.contract_number,
      region: form.region,
      departement: form.departement,
      commune: form.commune,
      quartier_village: form.quartier_village,
      niveau_service: form.niveau_service,
      frais_timbres: form.frais_timbres,
      frais_taxe: form.frais_taxe,
      frais_branchement: form.frais_branchement,
      frais_avance: form.frais_avance,
      lieu: form.lieu,
      date: form.date ? new Date(form.date).toLocaleDateString('fr-FR') : '',
    };

    const res = editingId
      ? await contractsService.update(editingId, payload)
      : await contractsService.generate(payload);

    const blob = new Blob([res.data], { type: 'application/pdf' });
    setPdfUrl(URL.createObjectURL(blob));

    const wasEditing = !!editingId;

    setContractsPage(0);
    loadContracts(0, contractsSearch);

    const finalNumber = res.headers?.['x-contract-number'] || form.contract_number;
    setLastGeneratedNumber(finalNumber);
    // ✅ Vide le formulaire automatiquement après ajout ou modification réussie
    // On garde le pdfUrl affiché un court instant avant reset, ou on le reset directement :
    if (!wasEditing) {
      resetForm();
    } else {
      setEditingId(null);
      resetForm();
    }
  } catch (e) {
    console.error(e);
    const msg = e.response?.data?.detail || (editingId
      ? "Erreur lors de la modification du contrat."
      : "Erreur lors de la génération du contrat.");
    alert(msg);
  } finally {
    setGenerating(false);
  }
};

// Génère la liste des numéros de page à afficher avec des "..." pour les pages masquées
const getPageNumbers = (current, total) => {
  const delta = 2; // nombre de pages visibles de chaque côté de la page actuelle
  const range = [];
  const withDots = [];
  let last;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (last !== undefined) {
      if (i - last === 2) {
        withDots.push(last + 1);
      } else if (i - last > 1) {
        withDots.push('...');
      }
    }
    withDots.push(i);
    last = i;
  });

  return withDots;
};

const loadContractForEdit = async (contractId) => {
  try {
    const res = await contractsService.getById(contractId);
    const c = res.data;
    const p = c.payload || {};

    setEditingId(c.id);
    setPdfUrl(null);

    if (c.customer_id) {
      await applyCustomer(c.customer_id);
    }

    if (c.meter_id) {
      try {
        const mRes = await metersService.getAll({ search: c.meter_id, limit: 1 });
        const m = (mRes.data.items || [])[0];
        if (m) {
          setSelectedMeter(m);
          setMeterSearch(`${m.meter_id}${m.installation_address ? ' — ' + m.installation_address : ''}`);
        }
      } catch (e) { /* silencieux */ }
    } else {
      setSelectedMeter(null);
      setMeterSearch('');
    }

    setForm(f => ({
      ...f,
      contract_number: c.contract_number,
      region: p.region || '',
      departement: p.departement || '',
      commune: p.commune || '',
      quartier_village: p.quartier_village || '',
      niveau_service: c.niveau_service || f.niveau_service,
      frais_timbres: p.frais_timbres || '',
      frais_taxe: p.frais_taxe || '',
      frais_branchement: p.frais_branchement || '',
      frais_avance: p.frais_avance || '',
      lieu: p.lieu || '',
    }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (e) {
    console.error(e);
    alert("Erreur lors du chargement du contrat.");
  }
};

const cancelEdit = () => {
  setEditingId(null);
  setPdfUrl(null);
  refreshNextNumber(form.prefix);
};

const handlePreview = async (contractId) => {
  try {
    const res = await contractsService.reprint(contractId);
    const blob = new Blob([res.data], { type: 'application/pdf' });
    setPdfUrl(URL.createObjectURL(blob));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (e) {
    alert("Erreur lors du chargement de l'aperçu.");
  }
};

const handleDelete = async (contractId, contractNumber) => {
  if (!window.confirm(`Supprimer définitivement le contrat ${contractNumber} ? Cette action est irréversible.`)) {
    return;
  }
  setDeletingId(contractId);
  try {
    await contractsService.remove(contractId);
    if (editingId === contractId) cancelEdit();
    loadContracts(contractsPage, contractsSearch);
  } catch (e) {
    alert("Erreur lors de la suppression du contrat.");
  } finally {
    setDeletingId(null);
  }
};
  const handleReprint = async (contractId) => {
    try {
      const res = await contractsService.reprint(contractId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      alert("Erreur lors de la réimpression.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(contractsTotal / PAGE_SIZE));

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="w-7 h-7 text-blue-700" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Contrats d'abonnement
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* -------- Formulaire -------- */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-5">
          {/* Recherche client */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                value={customerSearch}
                onChange={e => handleCustomerSearchChange(e.target.value)}
                onFocus={() => setShowCustomerSuggestions(true)}
                placeholder="Rechercher un client (nom, téléphone, ID)..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            {showCustomerSuggestions && customerSuggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {customerSuggestions.map(c => (
                  <button
                    key={c.id}
                    onClick={() => pickCustomer(c)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 dark:hover:bg-gray-600"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{c.full_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{c.customer_id} · {c.phone}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Recherche compteur — saisie libre, même logique que le client */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Compteur
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                value={meterSearch}
                onChange={e => handleMeterSearchChange(e.target.value)}
                onFocus={() => setShowMeterSuggestions(true)}
                placeholder="Rechercher un compteur (numéro, client, téléphone, adresse)..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            {showMeterSuggestions && meterSuggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {meterSuggestions.map(m => (
                  <button
                    key={m.meter_id}
                    onClick={() => pickMeter(m)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 dark:hover:bg-gray-600"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{m.meter_id}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {m.customer ? `${m.customer.full_name} · ${m.customer.phone}` : 'Sans client associé'}
                      {m.installation_address ? ` · ${m.installation_address}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selectedMeter && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" /> Compteur {selectedMeter.meter_id} sélectionné
              </p>
            )}
          </div>

          {customer && (
            <>
              {/* Aperçu infos auto-remplies */}
              <div className="grid grid-cols-2 gap-3 text-sm bg-blue-50 dark:bg-gray-700/50 rounded-lg p-3">
                <div><span className="text-gray-500">Nom :</span> <b>{customer.full_name}</b></div>
                <div><span className="text-gray-500">Téléphone :</span> <b>{customer.phone}</b></div>
                <div><span className="text-gray-500">{customer.id_card_type || 'CNI'} :</span> <b>{customer.id_card_number || '—'}</b></div>
                <div><span className="text-gray-500">Adresse :</span> <b>{customer.address || '—'}</b></div>
              </div>

              {/* Numéro de contrat */}
              <div className="grid grid-cols-3 gap-3 items-end">
               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Préfixe</label>
                  <select
                    value={form.prefix}
                    onChange={e => { updateField('prefix', e.target.value); refreshNextNumber(e.target.value); }}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {PREFIXES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">N° du contrat</label>
                  <input value={form.contract_number} onChange={e => updateField('contract_number', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                <button
                  type="button"
                  onClick={() => refreshNextNumber(form.prefix)}
                  title="Récupérer le prochain numéro disponible"
                  className="flex items-center justify-center gap-1 px-3 py-2 border rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <RefreshCw className="w-4 h-4" /> Actualiser
                </button>
              </div>

              {/* Localisation (éditable) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Région</label>
                  <input value={form.region} onChange={e => updateField('region', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Département</label>
                  <input value={form.departement} onChange={e => updateField('departement', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Commune</label>
                  <input value={form.commune} onChange={e => updateField('commune', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quartier/Village</label>
                  <input value={form.quartier_village} onChange={e => updateField('quartier_village', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
              </div>

              {/* Niveau de service */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Niveau de service souscrit
                </label>
                <select
                  value={form.niveau_service}
                  onChange={e => updateField('niveau_service', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {NIVEAUX.map(n => <option key={n.key} value={n.key}>{n.label}</option>)}
                </select>
              </div>

              {/* Frais de souscription */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Frais de souscription (F CFA)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Timbres fiscaux" value={form.frais_timbres}
                    onChange={e => updateField('frais_timbres', e.target.value)}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  <input placeholder="Taxe communale" value={form.frais_taxe}
                    onChange={e => updateField('frais_taxe', e.target.value)}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  <input placeholder="Branchement" value={form.frais_branchement}
                    onChange={e => updateField('frais_branchement', e.target.value)}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                  <input placeholder="Avance sur consommation" value={form.frais_avance}
                    onChange={e => updateField('frais_avance', e.target.value)}
                    className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
              </div>

              {/* Lieu et date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lieu de signature</label>
                  <input value={form.lieu} onChange={e => updateField('lieu', e.target.value)}
                    placeholder="Ex : Dakar"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                  <input type="date" value={form.date} onChange={e => updateField('date', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
              </div>

                <div className="flex gap-2">
                  {editingId && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="flex items-center gap-1 px-4 py-2.5 border rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <X className="w-4 h-4" /> Annuler
                    </button>
                  )}
                  <button
                    onClick={handleGenerate}
                    disabled={!canGenerate || generating || (editingId ? !canEdit : !canAdd)}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors"
                  >
                    {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
                    {generating ? 'Traitement...' : editingId ? 'Enregistrer les modifications' : 'Générer le contrat PDF'}
                  </button>
                </div>
            </>
          )}
        </div>

        {/* -------- Aperçu PDF -------- */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 min-h-[500px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800 dark:text-white">Aperçu</h2>
            {pdfUrl && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" /> Contrat généré
              </div>
            )}
          </div>
          {pdfUrl ? (
            <>
              <iframe title="Aperçu contrat" src={pdfUrl} className="flex-1 w-full rounded-lg border" />
              <a
              href={pdfUrl}
                download={`Contrat_${lastGeneratedNumber.replace(' ', '_')}.pdf`}
                className="mt-3 text-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-white py-2 rounded-lg font-medium"
              >
                Télécharger le PDF
              </a>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              Sélectionnez un client ou un compteur, remplissez le formulaire puis générez le contrat.
            </div>
          )}
        </div>
      </div>

      {/* -------- Table des contrats (paginée) -------- */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-semibold text-gray-800 dark:text-white text-lg">
            Historique des contrats {contractsTotal > 0 && <span className="text-gray-400 font-normal text-sm">({contractsTotal})</span>}
          </h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              value={contractsSearch}
              onChange={e => setContractsSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setContractsPage(0); loadContracts(0, contractsSearch); } }}
              placeholder="N° contrat, client, téléphone, N° compteur, adresse..."
              className="pl-9 pr-8 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white w-80"
            />
            {contractsSearch && (
              <button
                type="button"
                onClick={() => setContractsSearch('')}
                title="Effacer la recherche"
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <th className="py-2 pr-3">N° Contrat</th>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-3">Compteur</th>
                <th className="py-2 pr-3">Adresse</th>
                <th className="py-2 pr-3">Niveau</th>
                <th className="py-2 pr-3">Créé par</th>
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loadingContracts && (
                <tr><td colSpan={8} className="py-6 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td></tr>
              )}
              {!loadingContracts && contracts.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-gray-400">
                  {contractsSearch ? `Aucun contrat trouvé pour « ${contractsSearch} »` : 'Aucun contrat généré'}
                </td></tr>
              )}
              {!loadingContracts && contracts.map(c => (
                <tr key={c.id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">{c.contract_number}</td>
                  <td className="py-2 pr-3">
                    <div className="text-gray-900 dark:text-white">{c.customer_name || '—'}</div>
                    <div className="text-xs text-gray-400">{c.customer_phone}</div>
                  </td>
                  <td className="py-2 pr-3 font-mono text-gray-600 dark:text-gray-300">{c.meter_number || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{c.address || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{c.niveau_service || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{c.created_by_name || '—'}</td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                    {new Date(c.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="py-2 pr-3 text-right">
  <div className="flex items-center justify-end gap-3">
    <button onClick={() => handlePreview(c.id)} title="Aperçu" className="text-gray-500 hover:text-blue-600">
      <Eye className="w-4 h-4" />
    </button>
    <button onClick={() => handleReprint(c.id)} title="Télécharger" className="text-gray-500 hover:text-blue-600">
      <Download className="w-4 h-4" />
    </button>
    {canEdit && (
      <button onClick={() => loadContractForEdit(c.id)} title="Modifier" className="text-gray-500 hover:text-amber-600">
        <Pencil className="w-4 h-4" />
      </button>
    )}
    {canDelete && (
      <button
        onClick={() => handleDelete(c.id, c.contract_number)}
        disabled={deletingId === c.id}
        title="Supprimer"
        className="text-gray-500 hover:text-red-600 disabled:opacity-40"
      >
        {deletingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </button>
    )}
  </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}

<div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-3 text-sm text-gray-600 dark:text-gray-300">
  <span>
    Page {contractsPage + 1} / {totalPages} · {contractsTotal} contrats
  </span>

  <div className="flex items-center gap-1 flex-wrap justify-center">
    <button
      disabled={contractsPage === 0}
      onClick={() => setContractsPage(0)}
      title="Première page"
      className="flex items-center px-2 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
    >
      <ChevronsLeft className="w-4 h-4" />
    </button>
    <button
      disabled={contractsPage === 0}
      onClick={() => setContractsPage(p => Math.max(0, p - 1))}
      className="flex items-center px-2 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
    >
      <ChevronLeft className="w-4 h-4" />
    </button>

    {getPageNumbers(contractsPage + 1, totalPages).map((p, idx) =>
      p === '...' ? (
        <span key={`dots-${idx}`} className="px-2 text-gray-400 select-none">…</span>
      ) : (
        <button
          key={p}
          onClick={() => setContractsPage(p - 1)}
          className={`min-w-[32px] px-2 py-1.5 border rounded-lg ${
            p === contractsPage + 1
              ? 'bg-blue-600 text-white border-blue-600'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600'
          }`}
        >
          {p}
        </button>
      )
    )}

    <button
      disabled={contractsPage + 1 >= totalPages}
      onClick={() => setContractsPage(p => p + 1)}
      className="flex items-center px-2 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
    >
      <ChevronRight className="w-4 h-4" />
    </button>
    <button
      disabled={contractsPage + 1 >= totalPages}
      onClick={() => setContractsPage(totalPages - 1)}
      title="Dernière page"
      className="flex items-center px-2 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600"
    >
      <ChevronsRight className="w-4 h-4" />
    </button>
  </div>
</div>
      </div>
    </div>
  );
}